import { useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Printer } from 'lucide-react';
import { Button, Modal } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/axios';
import type { PdfMode } from '@/lib/pdf';
import { type FichePerimetre, openFicheMensuellePdf } from '@/services/etatsLocation';
import { toast } from '@/store/toastStore';

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

interface Props {
  /** Ce qu'on imprime : « ENG-003 · Pelle mécanique » ou « Tous les engins de Ets Benali ». */
  titre: string;
  /** Périmètre de la fiche (le mois se choisit dans la fenêtre). */
  perimetre: FichePerimetre;
  /** Mois proposé à l'ouverture. */
  moisInitial: Date;
  onClose: () => void;
}

/**
 * Fiche de pointage MENSUELLE : on choisit le mois, on imprime. Le document contient, pour chaque
 * machine, TOUS les jours du mois (travaillés, non travaillés, repos) avec les heures
 * supplémentaires, les heures perdues et le gasoil — une page A4 par machine.
 */
export function FicheMensuelleModal({ titre, perimetre, moisInitial, onClose }: Props) {
  const [mois, setMois] = useState(() => new Date(moisInitial.getFullYear(), moisInitial.getMonth(), 1));
  const [enCours, setEnCours] = useState<PdfMode | null>(null);
  const decaler = (n: number) => setMois((m) => new Date(m.getFullYear(), m.getMonth() + n, 1));

  const lancer = async (mode: PdfMode) => {
    setEnCours(mode);
    try {
      await openFicheMensuellePdf({ ...perimetre, annee: mois.getFullYear(), mois: mois.getMonth() + 1 }, mode);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setEnCours(null);
    }
  };

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Fiche de pointage mensuelle"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Fermer</Button>
          <Button variant="secondary" loading={enCours === 'download'} disabled={enCours !== null} onClick={() => lancer('download')}>
            <Download /> Télécharger
          </Button>
          <Button loading={enCours === 'open'} disabled={enCours !== null} onClick={() => lancer('open')}>
            <Printer /> Imprimer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="rounded-md border border-border bg-surface-primary/40 px-3 py-2 text-sm font-medium text-content-primary">{titre}</p>
        <div className="flex items-center justify-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => decaler(-1)} aria-label="Mois précédent"><ChevronLeft className="size-4" /></Button>
          <span className="min-w-[150px] text-center text-sm font-semibold text-content-primary">{MOIS[mois.getMonth()]} {mois.getFullYear()}</span>
          <Button variant="secondary" size="sm" onClick={() => decaler(1)} aria-label="Mois suivant"><ChevronRight className="size-4" /></Button>
        </div>
        <p className="text-xs leading-relaxed text-content-muted">
          Un tableau par machine, une page A4 chacune : <strong>tous les jours du mois</strong> — travaillés, non travaillés,
          repos — avec les heures supplémentaires, les heures perdues (panne, intempéries) et le gasoil, puis la synthèse
          du mois et les cadres de signature. Les jours à venir restent vides.
        </p>
      </div>
    </Modal>
  );
}
